import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import getChatClipboardImages from 'utils/getChatClipboardImages';

interface Props {
  initialFiles: File[];
  onClose: () => void;
  onSend: (files: File[]) => void;
}

const ImagePreview: FC<{ file: File; onRemove: () => void }> = ({ file, onRemove }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <Box pos="relative" minW={0}>
      <Image
        ignoreFallback
        src={url || undefined}
        alt={file.name}
        w="100%"
        h="140px"
        objectFit="contain"
      />
      <IconButton
        pos="absolute"
        top={0}
        right={0}
        size="sm"
        aria-label={`${t('delete')}: ${file.name}`}
        icon={<CloseIcon />}
        onClick={onRemove}
      />
      <Text fontSize="xs" overflowWrap="anywhere">{file.name}</Text>
    </Box>
  );
};

const PreviewChatImagesModal: FC<Props> = ({ initialFiles, onClose, onSend }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState(initialFiles);

  return (
    <Modal isOpen onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        onPaste={(event) => {
          const images = getChatClipboardImages(event.clipboardData);
          if (images.length > 0) {
            event.preventDefault();
            setFiles((previous) => [...previous, ...images]);
          }
        }}
      >
        <ModalHeader pr="48px">{t('chatImagesPreview')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={4}>{t('chatPasteMoreImages')}</Text>
          <SimpleGrid columns={2} spacing={4}>
            {files.map((file, index) => (
              <ImagePreview
                key={index}
                file={file}
                onRemove={() => setFiles((previous) => previous.filter((_, i) => i !== index))}
              />
            ))}
          </SimpleGrid>
          {files.length === 0 && <Text>{t('listIsEmpty')}</Text>}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button isDisabled={files.length === 0} onClick={() => onSend(files)}>
            {t('sendMessage')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PreviewChatImagesModal;
